import { InfoCircleOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'

import { darkBlueFontColor } from 'assets/styles/variables'
import {
  getIconBoxBackground,
  getIconBoxBorder,
  getIconColor,
} from 'features/trend-detection/features/benchmarking-agent/gap-color-utils'
import type { GapStrength } from 'features/trend-detection/features/benchmarking-agent/gap-strength-utils'

export type { GapStrength } from './gap-strength-utils'

type Direction = 'leading' | 'lagging'

const GAP_LABEL: Record<GapStrength, string> = {
  major: 'Major sentiment gap',
  moderate: 'Moderate sentiment gap',
  minor: 'Minor sentiment gap',
}

const GAP_SHORT_LABEL: Record<GapStrength, string> = {
  major: 'Major',
  moderate: 'Moderate',
  minor: 'Minor',
}

const GAP_TIERS: Array<{ key: GapStrength; tagline: string }> = [
  { key: 'minor', tagline: 'Small difference, may be normal variation.' },
  {
    key: 'moderate',
    tagline: 'Real difference, but smaller or less consistent.',
  },
  { key: 'major', tagline: 'Large, consistent difference worth acting on.' },
]

const SEVERITY_COLORS = {
  leading: { major: '#23896c', moderate: '#669614', minor: '#4f72a4' },
  lagging: { major: '#ef4444', moderate: '#d98200', minor: '#4f72a4' },
}

const DottedBar: React.FC<{ x: number; color: string }> = ({ x, color }) => (
  <>
    <rect x={x} y="1" width="1.5" height="3" rx="0.75" fill={color} />
    <rect x={x} y="6" width="1.5" height="3" rx="0.75" fill={color} />
    <rect x={x} y="11" width="1.5" height="3" rx="0.75" fill={color} />
  </>
)

const SolidBar: React.FC<{ x: number; color: string }> = ({ x, color }) => (
  <rect x={x} y="2" width="1.5" height="12" rx="0.5" fill={color} />
)

export const GapMeasurementIcon: React.FC<{
  direction: Direction
  color: string
  width: number
  height: number
}> = ({ direction, color, width, height }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ opacity: 0.8 }}
  >
    {direction === 'leading' ? (
      <>
        <DottedBar x={1} color={color} />
        <SolidBar x={13} color={color} />
        <line
          x1="4"
          y1="8"
          x2="11"
          y2="8"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
        />
        <polyline
          points="8.5,5.5 11.5,8 8.5,10.5"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </>
    ) : (
      <>
        <SolidBar x={1} color={color} />
        <DottedBar x={13} color={color} />
        <line
          x1="12"
          y1="8"
          x2="5"
          y2="8"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
        />
        <polyline
          points="7.5,5.5 4.5,8 7.5,10.5"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </>
    )}
  </svg>
)

export const PerformanceBadge: React.FC<{
  direction: Direction
  strength: GapStrength
}> = ({ direction, strength }) => {
  const iconColor = getIconColor(direction, strength)
  const severityColor = SEVERITY_COLORS[direction][strength]
  return (
    <BadgeRow>
      <IconBox
        $background={getIconBoxBackground(direction, strength)}
        $border={getIconBoxBorder(direction, strength)}
      >
        <GapMeasurementIcon
          direction={direction}
          color={iconColor}
          width={24}
          height={24}
        />
      </IconBox>
      <BadgeLabels>
        <TypeLabel>
          {direction === 'leading'
            ? 'Outperforms cohort'
            : 'Underperforms cohort'}
        </TypeLabel>
        <SeverityRow>
          <SeverityLabel $color={severityColor}>
            {GAP_LABEL[strength]}
          </SeverityLabel>
          <GapStrengthPopover strength={strength} direction={direction} />
        </SeverityRow>
      </BadgeLabels>
    </BadgeRow>
  )
}

// Legacy 3-bar icon — kept for GapStrengthBadge (detail page fallback)
export const GapStrengthIcon: React.FC<{
  strength: GapStrength
  color?: string
  width?: number
  height?: number
  /** When false, all bars render in muted grey — used by popover tier rows
   *  to mark the inactive tiers. Mirrors the emergence popover treatment. */
  active?: boolean
}> = ({
  strength,
  color: colorProp,
  width = 16,
  height = 12,
  active = true,
}) => {
  const color =
    colorProp ??
    (strength === 'major'
      ? '#059669'
      : strength === 'moderate'
        ? '#D97706'
        : '#9CA3AF')
  const inactiveColor = '#d9d9d9'
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="0"
        y="7"
        width="3.5"
        height="5"
        rx="0.8"
        fill={active ? color : inactiveColor}
      />
      <rect
        x="4.5"
        y="4"
        width="3.5"
        height="8"
        rx="0.8"
        fill={active ? color : inactiveColor}
      />
      <rect
        x="9"
        y="0"
        width="3.5"
        height="12"
        rx="0.8"
        fill={active && strength === 'major' ? color : inactiveColor}
      />
    </svg>
  )
}

export const GapStrengthBadge: React.FC<{ strength: GapStrength }> = ({
  strength,
}) => {
  const color =
    strength === 'major'
      ? '#059669'
      : strength === 'moderate'
        ? '#D97706'
        : '#9CA3AF'
  return (
    <Badge $color={color}>
      <GapStrengthIcon strength={strength} />
      {GAP_LABEL[strength]}
    </Badge>
  )
}

const GapStrengthPopover: React.FC<{
  strength: GapStrength
  direction: Direction
}> = ({ strength, direction }) => {
  const color = SEVERITY_COLORS[direction][strength]
  return (
    <GapStrengthPopup
      openOnHover
      nativeButton={false}
      initialFocus={false}
      content={
        <GapPopoverContent>
          <GapPopoverTitle>Gap strength</GapPopoverTitle>
          <GapPopoverDescription>
            Shows how large and reliable the sentiment gap is between the
            selected entity and the rest of the cohort.
          </GapPopoverDescription>
          <GapTierList>
            {GAP_TIERS.map(({ key }) => {
              const isActive = key === strength
              return (
                <GapTierRow key={key} $active={isActive} $color={color}>
                  <GapTierIconWrap>
                    <GapStrengthIcon
                      strength={key}
                      color="#475569"
                      width={24}
                      height={16}
                      active={isActive}
                    />
                  </GapTierIconWrap>
                  <GapTierLabel $active={isActive}>
                    {GAP_SHORT_LABEL[key]}
                  </GapTierLabel>
                </GapTierRow>
              )
            })}
          </GapTierList>
          <GapPopoverMetric>
            {GAP_TIERS.find((t) => t.key === strength)?.tagline}
          </GapPopoverMetric>
        </GapPopoverContent>
      }
    >
      <GapInfoIcon $color={color}>
        <InfoCircleOutlined />
      </GapInfoIcon>
    </GapStrengthPopup>
  )
}

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`

const IconBox = styled.span<{ $background: string; $border: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  flex-shrink: 0;
  border-radius: 5px;
  background: ${({ $background }) => $background};
  border: 0.86px solid ${({ $border }) => $border};
`

const BadgeLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const TypeLabel = styled.span`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-sm, 14px);
  font-weight: 600;
  line-height: var(--Line-height-text-sm, 20px);
  letter-spacing: 0;
  color: #1e293b;
`

const SeverityLabel = styled.span<{ $color: string }>`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-xs, 12px);
  font-weight: 600;
  line-height: var(--Line-height-text-xs, 16px);
  letter-spacing: 0;
  color: ${({ $color }) => $color};
`

const Badge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const SeverityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const GapPopoverContent = styled.div`
  padding: 4px 0;
`

const GapPopoverTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${darkBlueFontColor};
  margin-bottom: 6px;
`

const GapPopoverDescription = styled.p`
  font-size: 13px;
  color: ${darkBlueFontColor};
  line-height: 1.4;
  margin: 0 0 10px 0;
  opacity: 0.9;
`

const GapTierList = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 16px;
  margin-bottom: 10px;
`

const GapTierRow = styled.div<{ $active: boolean; $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 4px;
  padding-bottom: 4px;
  border-bottom: 3px solid ${(p) => (p.$active ? p.$color : 'transparent')};
`

const GapTierIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`

const GapTierLabel = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  color: ${(p) => (p.$active ? darkBlueFontColor : '#d9d9d9')};
`

const GapPopoverMetric = styled.p`
  font-size: 13px;
  color: ${darkBlueFontColor};
  line-height: 1.5;
  margin: 0;
`

const GapInfoIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: help;
  color: ${(p) => p.$color};
  background-color: ${(p) => `${p.$color}1f`};
  font-size: 12px;
`

const GapStrengthPopup = styled(Popover)`
  max-width: 320px;
`
